import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCashbackEntries, addCashbackEntry, deleteCashbackEntry } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash, CreditCard, TrendUp, CurrencyInr, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export default function CashbackTrackerPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [cardSummary, setCardSummary] = useState([]);
  const [grandSpend, setGrandSpend] = useState(0);
  const [grandCashback, setGrandCashback] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const initForm = {
    card_last4: '', card_bank: 'HDFC', month: '',
    total_spend: '', cashback_amount: '', cashback_type: 'ploughback',
    credit_date: new Date().toISOString().split('T')[0],
    reference: '', note: '',
  };
  const [form, setForm] = useState(initForm);

  const isAllowed = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'accountant';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCashbackEntries();
      setEntries(res.data.entries || []);
      setCardSummary(res.data.card_summary || []);
      setGrandSpend(res.data.grand_total_spend || 0);
      setGrandCashback(res.data.grand_total_cashback || 0);
    } catch (err) {
      toast.error('Load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAllowed) fetchData(); }, [fetchData, isAllowed]);

  const handleSubmit = async () => {
    if (!form.card_last4 || form.card_last4.length < 4) { toast.error('Card ke last 4 digits daalo'); return; }
    if (!form.cashback_amount || parseFloat(form.cashback_amount) <= 0) { toast.error('Cashback amount daalo'); return; }
    setSubmitting(true);
    try {
      const res = await addCashbackEntry({
        ...form,
        amount: parseFloat(form.cashback_amount),
        total_spend: parseFloat(form.total_spend || 0),
      });
      toast.success(res.data.message);
      setForm(initForm);
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await deleteCashbackEntry(id);
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pct = grandSpend > 0 ? (grandCashback / grandSpend * 100).toFixed(2) : '0.00';

  if (!isAllowed) return <div className="p-6 text-center text-red-600 font-medium" data-testid="cashback-access-denied">Access denied. Only Admin or CA can view Cashback Tracker.</div>;

  return (
    <div className="space-y-5" data-testid="cashback-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="cashback-title">Cashback Tracker</h1>
          <p className="text-sm text-gray-500 mt-0.5">Card-wise spend vs ploughback/cashback track karo</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#002FA7] hover:bg-[#002590] gap-2 self-start" data-testid="new-cashback-btn">
          <Plus size={18} /> New Entry
        </Button>
      </div>

      {/* Grand Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cashback-stats">
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1"><CreditCard size={14} /> Cards</div>
          <p className="text-lg font-bold text-gray-900">{cardSummary.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-red-600 mb-1 flex items-center gap-1"><CurrencyInr size={14} /> Total Spend</div>
          <p className="text-lg font-bold text-red-700">{fmt(grandSpend)}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-green-600 mb-1 flex items-center gap-1"><TrendUp size={14} /> Total Cashback</div>
          <p className="text-lg font-bold text-green-700">{fmt(grandCashback)}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <div className="text-xs text-blue-600 mb-1 flex items-center gap-1"><TrendUp size={14} /> Avg %</div>
          <p className="text-lg font-bold text-blue-700">{pct}%</p>
        </div>
      </div>

      {/* Card-wise Summary */}
      {cardSummary.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="card-summary">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><CreditCard size={16} className="text-[#002FA7]" /> Card-wise Summary</h2>
          </div>
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Card</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead className="text-right">Total Spend</TableHead>
                <TableHead className="text-right">Cashback</TableHead>
                <TableHead className="text-right">%</TableHead>
                <TableHead className="text-right">Entries</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cardSummary.map(c => (
                <TableRow key={c.card_last4} data-testid={`card-${c.card_last4}`}>
                  <TableCell className="font-mono font-bold text-sm">XX{c.card_last4}</TableCell>
                  <TableCell><span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{c.card_bank}</span></TableCell>
                  <TableCell className="text-right text-sm text-red-600">{fmt(c.total_spend)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-green-700">{fmt(c.total_cashback)}</TableCell>
                  <TableCell className="text-right text-sm font-medium text-blue-700">{c.cashback_pct}%</TableCell>
                  <TableCell className="text-right text-sm">{c.entries}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* New Entry Form */}
      {showForm && (
        <div className="bg-white border-2 border-[#002FA7]/20 rounded-lg p-4 space-y-3" data-testid="cashback-form">
          <h2 className="text-base font-semibold">New Cashback / Ploughback Entry</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Card Last 4 *</Label>
              <Input value={form.card_last4} onChange={e => setField('card_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="5062" maxLength={4} data-testid="cb-card" />
            </div>
            <div>
              <Label className="text-xs">Bank</Label>
              <select value={form.card_bank} onChange={e => setField('card_bank', e.target.value)} className="w-full border rounded-md h-10 px-3 text-sm" data-testid="cb-bank">
                <option value="HDFC">HDFC</option>
                <option value="SBI">SBI</option>
                <option value="ICICI">ICICI</option>
                <option value="Axis">Axis</option>
                <option value="Kotak">Kotak</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Month</Label>
              <Input value={form.month} onChange={e => setField('month', e.target.value)} placeholder="Apr 2026" data-testid="cb-month" />
            </div>
            <div>
              <Label className="text-xs">Credit Date</Label>
              <Input type="date" value={form.credit_date} onChange={e => setField('credit_date', e.target.value)} data-testid="cb-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Total Spend (Month)</Label>
              <Input type="number" value={form.total_spend} onChange={e => setField('total_spend', e.target.value)} placeholder="6210790" data-testid="cb-spend" />
            </div>
            <div>
              <Label className="text-xs">Cashback Amount *</Label>
              <Input type="number" value={form.cashback_amount} onChange={e => setField('cashback_amount', e.target.value)} placeholder="62107" data-testid="cb-amount" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <select value={form.cashback_type} onChange={e => setField('cashback_type', e.target.value)} className="w-full border rounded-md h-10 px-3 text-sm" data-testid="cb-type">
                <option value="ploughback">Ploughback (Monthly)</option>
                <option value="cashback">Cashback (Per Txn)</option>
                <option value="reward">Reward Points</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Reference</Label>
              <Input value={form.reference} onChange={e => setField('reference', e.target.value)} placeholder="Bank email ref" data-testid="cb-ref" />
            </div>
          </div>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-xs">Note</Label>
              <Input value={form.note} onChange={e => setField('note', e.target.value)} placeholder="Remark..." data-testid="cb-note" />
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 gap-1" data-testid="submit-cashback-btn">
              <Plus size={14} /> {submitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {/* Entries Table */}
      <div className="bg-white border rounded-lg overflow-hidden" data-testid="cashback-entries">
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2">
          <CalendarBlank size={16} className="text-gray-500" />
          <h2 className="text-sm font-semibold">All Entries ({entries.length})</h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <CreditCard size={40} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Koi cashback entry nahi hai</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Cashback</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(e => (
                  <TableRow key={e.id} data-testid={`cb-row-${e.id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{e.credit_date}</TableCell>
                    <TableCell className="font-mono font-bold text-sm">XX{e.card_last4} <span className="text-xs text-gray-400 font-normal">{e.card_bank}</span></TableCell>
                    <TableCell className="text-sm">{e.month || '-'}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded ${e.cashback_type === 'ploughback' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'}`}>
                        {e.cashback_type}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm text-red-600">{e.total_spend > 0 ? fmt(e.total_spend) : '-'}</TableCell>
                    <TableCell className="text-right text-sm font-semibold text-green-700">{fmt(e.cashback_amount)}</TableCell>
                    <TableCell className="text-xs text-gray-500 max-w-[120px] truncate">{e.reference || '-'}</TableCell>
                    <TableCell className="text-xs text-gray-500 max-w-[120px] truncate">{e.note || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        onClick={() => handleDelete(e.id)} data-testid={`delete-cb-${e.id}`}>
                        <Trash size={15} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
